import {AxiosRequestConfig} from 'axios';
import dayjs from 'utils/dayjs';
import {spotheroPost} from 'api';
import {apiDateFormat} from 'hooks/config';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {
    ORDER,
    RESERVATION_STATUS,
    RESERVATION_TYPE,
    SORT_BY,
} from './constants';
import {ApiResponse} from 'api/api.types';
import {
    ReservationResponse,
    GetReservationsParams,
    FormattedReservationsResponse,
    FormattedReservationRequest,
} from './reservations.types';

/**
 * Formats the response from the get reservations endpoint so that it can be consumed by the UI
 * @param response - The response from the get reservations endpoint
 * @return - The formatted response
 */
export const formatGetReservationsResponse = (
    response: ApiResponse<ReservationResponse[]>
): FormattedReservationsResponse => {
    const formattedResponse: FormattedReservationsResponse =
        camelizeKeys(response);

    return formattedResponse;
};

/**
 * Formats the request for the get reservations endpoint
 * @param request - The request to the get reservations endpoint
 * @return - The formatted request
 */
export const formatGetReservationsRequest = (
    request: GetReservationsParams
) => {
    const updatedRequest = {
        filters: {
            reservationTypes:
                request.reservationTypes !== RESERVATION_TYPE.all
                    ? request.reservationTypes
                    : '',
            statuses:
                request.statuses !== RESERVATION_STATUS.all
                    ? request.statuses
                    : '',
            ...(request.eventId && {eventId: request.eventId}),
        },
        dateFrom: request.dateFrom
            ? dayjs(request.dateFrom).format(apiDateFormat)
            : '',
        dateTo: request.dateTo
            ? dayjs(request.dateTo).format(apiDateFormat)
            : '',
        limit: request.limit || 25,
        offset: request.offset || 0,
        sortBy: request.sortBy || SORT_BY.starts,
        order: request.order || ORDER.desc,
        searchText: request.searchText || '',
    };

    const formattedRequest: FormattedReservationRequest = {
        ...convertKeysToSnakeCase(updatedRequest),
        facilityIDs: request.facilityIds.toString(),
    };

    return formattedRequest;
};

/**
 * Gets an operator's reservations.
 *
 * @param params - The parameters required to fetch reservations.
 * @param params.facilityIds - The IDs of the facilities for which reservations are being fetched.
 * @param params.dateFrom - The start date for the reservations.
 * @param params.dateTo - The end date for the reservations.
 * @param params.reservationTypes - The types of reservations to include.
 * @param params.statuses - The statuses of reservations to filter by.
 * @param params.searchText - Text to filter reservations by, such as customer name or reservation ID.
 * @param params.offset - The pagination offset for the reservations.
 * @param params.limit - The maximum number of reservations to return.
 * @param params.sortBy - The field to sort the reservations by.
 * @param params.order - The order to sort the reservations in.
 * @param config - Additional Axios configuration options for the request.
 * @param config.signal - An abort signal to cancel the request.
 * @returns - A promise that resolves to the formatted reservations data.
 */
const getReservations = async (
    params: GetReservationsParams,
    config: AxiosRequestConfig = {}
) => {
    try {
        const formattedParams = formatGetReservationsRequest(params);

        const response = await spotheroPost<ReservationResponse[]>(
            'operator-reservations/',
            formattedParams,
            config,
            false
        );

        const formattedResponse = formatGetReservationsResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default getReservations;
